/**
 * One athlete application or pitch waiting on the brand: who, what they asked for, what they
 * said, and Accept / Counter / Pass. Lives in the Campaigns hub's Incoming tab.
 */
import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { MoreHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ApplicationActions } from '@/components/applications/ApplicationActions'
import { money } from '@/lib/dealTypes'
import type { Application } from '@/redux/marketerApi'

const followers = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1000
      ? `${Math.round(n / 1000)}K`
      : String(n)

const when = (iso: string) => {
  const d = new Date(iso)
  const days = Math.floor((Date.now() - d.getTime()) / 86400000)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export const SubmissionCard: React.FC<{ item: Application }> = ({ item }) => {
  const navigate = useNavigate()
  const a = item.athlete
  const collabPath = item.campaignId
    ? `/campaign/${item.campaignId}/athlete/${item.id}`
    : `/collaborations/${item.id}`
  const initials = (a.name ?? '?')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <li className="rounded-2xl border border-border bg-card p-4 shadow-sm md:p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        {/* Athlete */}
        <Link
          to={`/athlete/${a.id}?application=${item.id}`}
          className="flex min-w-0 items-center gap-4 lg:w-72 lg:shrink-0 lg:border-r lg:border-border lg:pr-5"
        >
          <Avatar className="h-16 w-16 border border-border">
            <AvatarImage src={a.avatar ?? undefined} alt="" />
            <AvatarFallback className="bg-muted text-foreground">{initials}</AvatarFallback>
          </Avatar>
          <span className="min-w-0">
            <span className="block truncate text-lg font-semibold text-foreground">{a.name}</span>
            <span className="block truncate text-xs text-gray-600">
              {[a.sports[0], a.school].filter(Boolean).join(' · ')}
            </span>
            {a.followers ? (
              <span className="block text-xs text-gray-500">
                {followers(a.followers)} followers
              </span>
            ) : null}
          </span>
        </Link>

        {/* What they applied to and what they said */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(collabPath)}
              className="truncate text-left font-semibold text-foreground hover:text-[#3F7A0A]"
            >
              {item.title}
            </button>
            <Badge variant="outline" className="border-foreground/15 text-gray-700">
              {item.dealTypeLabel}
            </Badge>
            {item.source === 'pitch' ? (
              <Badge className="bg-purple-500/20 text-purple-800 hover:bg-purple-500/20">
                Pitch
              </Badge>
            ) : item.source === 'quote' ? (
              <Badge className="bg-[#C0FD72] text-black hover:bg-[#C0FD72]">Their rate</Badge>
            ) : item.source === 'counter' ? (
              <Badge className="bg-[#FFF4E5] text-[#B45309] hover:bg-[#FFF4E5]">Counter</Badge>
            ) : null}
            <span className="text-xs text-gray-500">{when(item.createdAt)}</span>
          </div>
          {item.booking ? (
            <p className="mt-1 text-sm text-gray-500">
              You asked for:{' '}
              <span className="text-gray-700">
                {item.booking.kind === 'custom'
                  ? item.booking.description
                  : `${item.booking.quantity} × ${item.booking.deliverableLabel}${item.booking.theme ? ` · ${item.booking.theme.label}` : ''}`}
              </span>
            </p>
          ) : null}
          <p className="mt-1 text-sm text-gray-700">
            {item.brand?.name ? `${item.brand.name} · ` : ''}
            {item.source === 'quote'
              ? 'Quoted'
              : item.source === 'counter'
                ? 'Countered at'
                : 'Asking'}{' '}
            <span className="font-semibold text-foreground">{money(item.ask)}</span>
            {item.statedPay != null && item.ask != null && item.statedPay !== item.ask ? (
              <span className="text-gray-500">
                {' '}
                (you {item.source === 'counter' ? 'offered' : 'posted'} {money(item.statedPay)})
              </span>
            ) : null}
          </p>
          {item.message ? (
            <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-gray-600">
              “{item.message}”
            </p>
          ) : null}
        </div>

        {/* Decide */}
        <div className="flex items-start gap-2 lg:w-[300px] lg:shrink-0 lg:border-l lg:border-border lg:pl-5">
          <ApplicationActions
            stacked
            applicationId={item.id}
            athleteName={a.name}
            dealType={item.dealType}
            ask={item.ask}
            size="sm"
            className="min-w-0 flex-1"
            onDone={(status) => {
              if (status === 'accepted') navigate(collabPath)
            }}
          />
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="More"
              className="shrink-0 rounded-lg border border-border p-2.5 text-muted-foreground hover:bg-muted"
            >
              <MoreHorizontal className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => navigate(collabPath)}>
                Open the deal
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => navigate(`/athlete/${a.id}?application=${item.id}`)}
              >
                View {a.name?.split(' ')[0]}'s shop
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </li>
  )
}

/**
 * The Submissions inbox: athletes who applied to an opportunity or pitched a brand, waiting on
 * a yes, a counter, or a pass.
 */

export default SubmissionCard
